import React, { useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useConfiguracionInicial } from "../Hooks/useConfiguracionInicial";
import { CONFIGURACION_INICIAL_RUTA } from "../Utils/configuracionInicial.constants";
import { MarcaConfiguracionInicial } from "./MarcaConfiguracionInicial";
import "../configuracionInicial.css";

export const ConfiguracionInicialGate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    bienvenidaVisible,
    guardando,
    error,
    cerrarBienvenida,
    iniciarYCargar,
    posponer,
    omitirTodo,
  } = useConfiguracionInicial();
  const [confirmandoOmision, setConfirmandoOmision] = useState(false);
  const [motivo, setMotivo] = useState("");

  const entrar = async () => {
    try {
      await iniciarYCargar();
      cerrarBienvenida();
      navigate(CONFIGURACION_INICIAL_RUTA);
    } catch (_error) {
      // El provider conserva el error y la bienvenida permanece visible.
    }
  };

  const salirPorAhora = async () => {
    try {
      await posponer();
      const rutaActual = location.pathname.replace(/\/+$/, "").toLowerCase();
      if (rutaActual === CONFIGURACION_INICIAL_RUTA.toLowerCase()) navigate("/");
    } catch (_error) {
      // El provider conserva el error y permite volver a intentar.
    }
  };

  const confirmarOmision = async () => {
    if (motivo.trim().length < 10) return;
    try {
      await omitirTodo(motivo.trim());
      setConfirmandoOmision(false);
      setMotivo("");
      const rutaActual = location.pathname.replace(/\/+$/, "").toLowerCase();
      if (rutaActual === CONFIGURACION_INICIAL_RUTA.toLowerCase()) navigate("/");
    } catch (_error) {
      // El provider conserva el error para mostrarlo sin bloquear el resto del ERP.
    }
  };

  return (
    <Modal
      show={bienvenidaVisible}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
      dialogClassName="ci-welcome-dialog"
      contentClassName="ci-welcome-modal"
      aria-labelledby="ci-welcome-title"
    >
      <Modal.Header className="ci-welcome-header">
        <div className="ci-welcome-brand">
          <MarcaConfiguracionInicial />
          <span>Configuración inicial</span>
        </div>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="ci-welcome-error">No pudimos completar la acción. Inténtalo nuevamente.</Alert>
        )}
        {!confirmandoOmision ? (
          <div className="ci-welcome-layout">
            <div className="ci-welcome-intro">
              <span className="ci-welcome-eyebrow">Empieza con una base clara</span>
              <Modal.Title as="h1" id="ci-welcome-title">
                Configuremos el sistema para la forma en que trabaja tu empresa
              </Modal.Title>
              <p className="ci-welcome-lead">
                Cuéntanos cómo funciona tu negocio y prepararemos la configuración inicial
                que necesitas para comenzar con menos ajustes manuales.
              </p>
              <div className="ci-welcome-benefits" role="list" aria-label="Ventajas del asistente">
                <div className="ci-welcome-benefit" role="listitem">
                  <span aria-hidden="true">✓</span>
                  <div><strong>Preguntas claras</strong><p>Solo te pediremos decisiones que puedas reconocer en tu operación.</p></div>
                </div>
                <div className="ci-welcome-benefit" role="listitem">
                  <span aria-hidden="true">↻</span>
                  <div><strong>A tu ritmo</strong><p>Puedes salir, regresar y continuar desde el punto donde quedaste.</p></div>
                </div>
                <div className="ci-welcome-benefit" role="listitem">
                  <span aria-hidden="true">◇</span>
                  <div><strong>Siempre bajo tu control</strong><p>Después podrás ajustar cada configuración directamente en Citrus.</p></div>
                </div>
              </div>
            </div>
            <div className="ci-welcome-overview" aria-label="Cómo funciona">
              <span className="ci-welcome-overview__tag">Una guía sencilla</span>
              <h2>¿Qué haremos?</h2>
              <ol className="ci-welcome-steps">
                <li><span>1</span><div><strong>Conocer tu operación</strong><p>Empresa, sucursales, ventas, pagos y contabilidad.</p></div></li>
                <li><span>2</span><div><strong>Preparar tus ajustes</strong><p>Usaremos tus respuestas para definir la configuración disponible.</p></div></li>
                <li><span>3</span><div><strong>Dejarte listo para empezar</strong><p>Al finalizar podrás aplicar la configuración confirmada.</p></div></li>
              </ol>
              <p className="ci-welcome-overview__note">No necesitas tener todas las respuestas ahora. Tu avance se conserva.</p>
            </div>
          </div>
        ) : (
          <div className="ci-welcome-omit" role="alert">
            <span className="ci-welcome-omit__icon" aria-hidden="true">!</span>
            <div>
              <h1 id="ci-welcome-title">¿Deseas omitir el asistente?</h1>
              <p>Esta decisión cierra el recorrido definitivamente. El asistente dejará de mostrarse y no podrás reabrir este proceso.</p>
            <Form.Group className="mt-3" controlId="motivo-omision-configuracion-inicial">
              <Form.Label>Cuéntanos brevemente por qué deseas omitirlo</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                maxLength={500}
                value={motivo}
                onChange={(evento) => setMotivo(evento.target.value)}
                placeholder="Escribe un motivo de al menos 10 caracteres."
              />
              <Form.Text>{motivo.trim().length}/10 caracteres mínimos</Form.Text>
            </Form.Group>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className={`ci-welcome-actions${confirmandoOmision ? " is-confirming" : ""}`}>
        {!confirmandoOmision && (
          <>
            <Button variant="link" className="ci-welcome-skip" disabled={guardando} onClick={() => setConfirmandoOmision(true)}>
              No volver a mostrar este asistente
            </Button>
            <div className="ci-welcome-actions__primary">
            <Button className="ci-welcome-secondary" variant="outline-secondary" disabled={guardando} onClick={salirPorAhora}>
                Ir a Citrus por ahora
              </Button>
            <Button className="ci-welcome-primary" variant="primary" disabled={guardando} onClick={entrar}>
              {guardando && <Spinner animation="border" size="sm" className="me-2" />}
                Empezar configuración <span aria-hidden="true">→</span>
            </Button>
            </div>
          </>
        )}
        {confirmandoOmision && (
          <div className="ci-welcome-actions__primary">
            <Button className="ci-welcome-secondary" variant="outline-secondary" disabled={guardando} onClick={() => setConfirmandoOmision(false)}>
              Volver
            </Button>
            <Button className="ci-welcome-danger" variant="danger" disabled={guardando || motivo.trim().length < 10} onClick={confirmarOmision}>
              {guardando && <Spinner animation="border" size="sm" className="me-2" />}
              Omitir definitivamente
            </Button>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
};
